import { parseCsv } from '@core/units/stockImport';

/**
 * The workbook, opened on this computer — no file goes to the server (no endpoint accepts one).
 * ExcelJS is loaded only when a file is chosen, so nobody else downloads it.
 *
 * <p>
 * What comes out is the plain grid the core plans from (`units/stockImport.ts`), and each picture
 * drawn on a row as a key into `pictures`. Pictures placed <em>in</em> a cell (Excel's newer "Place in
 * cell") are not read by ExcelJS; a file-name column covers those sheets.
 * </p>
 */

/** Sheets larger than this are refused with a reason, rather than freezing the tab. */
export const maxRows = 5000;

export interface ReadSheet {
  name: string;
  /** Every row as text, the first row at index 0. */
  grid: string[][];
  /** The picture key drawn over each row index. */
  pictureRows: Map<number, string>;
}

export interface ReadWorkbook {
  fileName: string;
  sizeBytes: number;
  sheets: ReadSheet[];
  /** Each picture's bytes by key, ready to send as a design's photo. */
  pictures: Map<string, File>;
}

const imageTypes = { png: 'image/png', jpeg: 'image/jpeg', gif: 'image/gif' } as const;

/** Why a file cannot be read, in words for the person. */
export class WorkbookProblem extends Error {}

export async function readWorkbook(file: File): Promise<ReadWorkbook> {
  const extension = file.name.toLowerCase().split('.').pop() ?? '';

  if (extension === 'csv') {
    const grid = parseCsv(await file.text());

    return {
      fileName: file.name,
      sizeBytes: file.size,
      sheets: [{ name: file.name.replace(/\.csv$/i, ''), grid, pictureRows: new Map() }],
      pictures: new Map(),
    };
  }

  if (extension === 'xls') {
    throw new WorkbookProblem(
      'This is the old Excel format (.xls). Open it in Excel and save it as .xlsx, then choose it again.',
    );
  }

  if (extension !== 'xlsx') {
    throw new WorkbookProblem('Choose an Excel file (.xlsx) or a .csv file.');
  }

  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  try {
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch {
    throw new WorkbookProblem('That file could not be opened as an Excel workbook.');
  }

  const pictures = new Map<string, File>();
  const sheets: ReadSheet[] = [];

  workbook.worksheets.forEach((worksheet, sheetIndex) => {
    const grid: string[][] = [];
    const width = worksheet.columnCount;

    for (
      let rowNumber = 1;
      rowNumber <= Math.min(worksheet.rowCount, maxRows + 50);
      rowNumber += 1
    ) {
      const row = worksheet.getRow(rowNumber);
      const cells: string[] = [];

      for (let column = 1; column <= width; column += 1) {
        cells.push(cellText(row.getCell(column)));
      }

      grid.push(cells);
    }

    const pictureRows = new Map<number, string>();

    worksheet.getImages().forEach((image, imageIndex) => {
      const media = workbook.getImage(Number(image.imageId));
      const bytes = media.buffer as unknown as Uint8Array | undefined;

      if (!bytes) {
        return;
      }

      // The row the picture's middle sits on: a photo drawn a little over its row's edge still
      // belongs to the row it was placed for.
      const top = image.range.tl.row;
      const bottom = image.range.br?.row ?? top + 1;
      const rowIndex = Math.floor(top + (bottom - top) / 2);

      if (pictureRows.has(rowIndex)) {
        return;
      }

      const key = `pic:${sheetIndex}:${imageIndex}`;
      const type = imageTypes[media.extension] ?? 'image/png';

      pictureRows.set(rowIndex, key);
      pictures.set(
        key,
        new File([bytes], `${worksheet.name}-row-${rowIndex + 1}.${media.extension}`, { type }),
      );
    });

    sheets.push({ name: worksheet.name, grid: trimGrid(grid), pictureRows });
  });

  return { fileName: file.name, sizeBytes: file.size, sheets, pictures };
}

/** A cell as the person sees it: a formula's result, a link's text, a date as a date. */
function cellText(cell: { value: unknown; text: string }): string {
  const value = cell.value;

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  try {
    return (cell.text ?? '').trim();
  } catch {
    return '';
  }
}

/** Trailing empty rows off — a sheet formatted down to row 1000 has 1000 rows. */
function trimGrid(grid: string[][]): string[][] {
  let end = grid.length;

  while (end > 0 && grid[end - 1]!.every((value) => value === '')) {
    end -= 1;
  }

  return grid.slice(0, end);
}

/** Rows handed back as a workbook to save — the rows left out, or the sheet with its new codes. */
export async function saveWorkbook(
  fileName: string,
  sheetName: string,
  headers: readonly string[],
  rows: readonly (readonly string[])[],
): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName.slice(0, 31) || 'Sheet1');

  sheet.addRow([...headers]).font = { bold: true };
  rows.forEach((row) => sheet.addRow([...row]));
  sheet.columns.forEach((column) => {
    column.width = 18;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
